// Validation schema
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useAuth} from "@/auth/useAuth";
import {useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {View, Text, StyleSheet} from "react-native";
import {SubmitButton} from "@/components/form/SubmitButton";
import {ErrorMessage} from "@/components/common/ErrorMessage";
import {TextInput} from "@/components/form/TextInput";
import {ThemeType} from "@/theming/baseTheme";
import {Card} from "@/components/common/Card";
import {z} from "zod";
import {useTranslation} from "react-i18next";
import {Image} from "expo-image";
import {AnimatedImage} from "@/components/common/AnimatedImage";

const loginSchema = z.object({
    email: z
        .string()
        .nonempty()
        .email(),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginProps {
    onSuccess?: (otpId: string, email: string) => void;
    style?: any;
}

export function Login({onSuccess, style}: LoginProps) {
    const {theme} = useCurrentTheme();
    const {requestOTP} = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const {t} = useTranslation('common');

    const {
        control,
        handleSubmit,
        formState: {errors, isValid},
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        mode: 'onChange',
        defaultValues: {
            email: '',
        },
    });

    const styles = createStyles(theme);

    const onSubmit = async (data: LoginFormData) => {
        if (isLoading) return;

        setIsLoading(true);
        setSubmitError(null);
        const email = data.email.toLowerCase();

        try {
            const otpId = await requestOTP(email);
            onSuccess?.(otpId, email);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'An error occurred';
            setSubmitError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card style={style}>
            <View style={styles.header}>
                <AnimatedImage source={require("@/assets/images/splash-icon.png")} style={styles.image} />
                <Text style={styles.title}>{t('login.title')}</Text>
                <Text style={styles.subtitle}>
                    {t('login.description')}
                </Text>
            </View>

            <View style={styles.form}>
                {submitError && (
                    <ErrorMessage message={submitError} style={styles.errorMessage}/>
                )}

                <Controller
                    control={control}
                    name="email"
                    render={({field: {onChange, onBlur, value}}) => (
                        <TextInput
                            label={t('login.input.label')}
                            placeholder={t('login.input.placeholder')}
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="email-address"
                            autoCapitalize="none"
                            autoComplete="email"
                            textContentType="emailAddress"
                            error={errors.email?.message}
                            editable={!isLoading}
                        />
                    )}
                />

                <SubmitButton
                    title={isLoading ? t('login.button.loading') : t('login.button.label')}
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid || isLoading}
                    loading={isLoading}
                    style={styles.submitButton}
                />
            </View>
        </Card>
    );
}
;

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        image: {
            width: 100,
            height: 100,
            alignSelf: 'center',
            marginVertical: theme.spacing.md,
        },
        header: {
            marginBottom: theme.spacing.xl,
            alignItems: 'center',
        },
        title: {
            fontSize: theme.fontSize.xxl,
            fontWeight: 'bold',
            color: theme.colors.text,
            marginBottom: theme.spacing.sm,
            textAlign: 'center',
        },
        subtitle: {
            fontSize: theme.fontSize.md,
            color: theme.colors.textSecondary,
            textAlign: 'center',
            lineHeight: theme.fontSize.md * 1.4,
        },
        form: {
            gap: theme.spacing.lg,
        },
        errorMessage: {
            marginBottom: theme.spacing.sm,
        },
        submitButton: {
            marginTop: theme.spacing.md,
        },
    });
